import { truncateMeta } from "@/lib/formatCopy";

export const SITE = {
    name: "Global Girmitiya Council",
    shortName: "Girmitiya Council",
    legalName: "Global Girmitiya Council",
    tagline: "Preserving the history and heritage of the Girmitiya diaspora",
    description:
        "The Global Girmitiya Council documents, preserves, and shares the history of Indian indentured labour and diaspora communities across sixteen nations from 1834 to 1917.",
    url: process.env.NEXT_PUBLIC_SITE_URL || "https://girmitiya.org.in",
    locale: "en_GB",
    language: "en",
    email: "info@girmitiyacouncil.in",
    phone: "+91-94159-49494",
    ogImage: "/home/banner-hero.webp",
    twitterHandle: "",
    themeColor: "#0F1C2E",
    keywords: [
        "Girmitiya",
        "indentured labour",
        "Indian diaspora",
        "indenture history",
        "Girmitiya archive",
        "Mauritius indenture",
        "Fiji Girmitiya",
        "Caribbean Indian heritage",
    ],
};

export const DEFAULT_SEO = {
    title: SITE.name,
    description: SITE.description,
    path: "/",
    image: SITE.ogImage,
    type: "website",
};

/** Static page SEO definitions */
export const PAGE_SEO = {
    home: {
        title: `${SITE.name} | Girmitiya History & Digital Archive`,
        description: SITE.description,
        path: "/",
    },
    about: {
        title: `About Us`,
        description:
            "Learn about the origins, mission, and core values of the Global Girmitiya Council and our work to preserve indenture-era history.",
        path: "/about",
    },
    contact: {
        title: `Contact Us`,
        description:
            "Contact the Global Girmitiya Council for research collaboration, archival submissions, media enquiries, and general information.",
        path: "/contact",
    },
    archives: {
        title: `Digital Archive`,
        description:
            "Explore the Girmitiya digital archive: indenture contracts, ship manifests, photographs, oral histories, and research publications.",
        path: "/archives",
    },
    countries: {
        title: `Global Diaspora Directory`,
        description:
            "Explore sixteen Girmitiya nations: historical records, timelines, and heritage for each indenture destination across the world.",
        path: "/countries",
    },
    events: {
        title: `Events & Gatherings`,
        description:
            "Discover symposiums, exhibitions, and cultural gatherings documenting the Girmitiya diaspora hosted by the Global Girmitiya Council.",
        path: "/events",
    },
    publications: {
        title: `Publications & Research`,
        description:
            "Peer-reviewed works, books, theses, and the Girmitiya Council Journal. Open access research on indenture and diaspora studies.",
        path: "/publications",
    },
    reports: {
        title: `Reports Database`,
        description:
            "Search government, NGO, and academic reports on indentured labour across fifteen Girmitiya nations and global diaspora studies.",
        path: "/reports",
    },
    girmitiyaProud: {
        title: `Girmitiya Proud | Global Heritage & Diaspora Legacy`,
        description:
            "Celebrating the global impact, heritage, literature, political leadership, and achievements of the Girmitiya Indian diaspora across 60 curated sections.",
        path: "/girmitiya-proud",
    },
    governingCouncil: {
        title: `Governing Council`,
        description:
            "Meet the Governing Council of the Global Girmitiya Council leading global initiatives for indenture heritage preservation.",
        path: "/governing-council",
    },
    teamAdvisory: {
        title: `Advisory Board`,
        description:
            "Meet the Advisory Board guiding the Global Girmitiya Council with academic rigour and strategic heritage expertise.",
        path: "/team/advisory-board",
    },
};

export function absoluteUrl(path = "") {
    const base = SITE.url.replace(/\/$/, "");
    if (!path) return base;
    const normalized = path.startsWith("/") ? path : `/${path}`;
    return `${base}${normalized}`;
}

export function buildTitle(pageTitle) {
    if (!pageTitle) return SITE.name;
    if (pageTitle.includes(SITE.name) || pageTitle.includes(SITE.shortName)) {
        return pageTitle;
    }
    return `${pageTitle} | ${SITE.shortName}`;
}

export function seoFromPage(pageKey) {
    const page = PAGE_SEO[pageKey];
    if (!page) return DEFAULT_SEO;
    return {
        title: buildTitle(page.title),
        description: truncateMeta(page.description),
        path: page.path,
        image: SITE.ogImage,
        type: "website",
    };
}

export function seoForCountry(country) {
    const desc = country.subtitle || country.description || SITE.description;
    return {
        title: buildTitle(`${country.name} Girmitiya History`),
        description: truncateMeta(desc),
        path: `/countries/${country.slug}`,
        image: country.heroImage || country.cardImage || SITE.ogImage,
        type: "article",
    };
}

export function seoForEvent(event) {
    return {
        title: buildTitle(event.title),
        description: truncateMeta(event.summary || event.description?.[0]),
        path: `/events/${event.slug}`,
        image: event.image || SITE.ogImage,
        type: "article",
    };
}

export function seoForReport(report) {
    return {
        title: buildTitle(report.title),
        description: truncateMeta(report.abstract),
        path: `/reports/${report.slug}`,
        image: SITE.ogImage,
        type: "article",
    };
}

export function organizationJsonLd() {
    return {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: SITE.legalName,
        url: SITE.url,
        logo: absoluteUrl("/brand/girmitiya-logo.webp"),
        description: SITE.description,
        email: SITE.email,
        telephone: SITE.phone,
        sameAs: [],
    };
}

export function websiteJsonLd() {
    return {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: SITE.name,
        url: SITE.url,
        description: SITE.description,
        inLanguage: SITE.language,
        publisher: { "@type": "Organization", name: SITE.legalName },
    };
}

export function breadcrumbJsonLd(items) {
    return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: items.map((item, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: item.name,
            item: absoluteUrl(item.path),
        })),
    };
}
